import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createMessageGroups } from "./tools";
import MessageGroup from "./MessageGroup";
import { useChatMessages } from "../../chatMessages";
import { useRoomSettings } from "../../context";
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/system";
import { Scrollbar } from "react-scrollbars-custom";
import { useAsyncReference, useResizeObserver } from "conference/tools";
import { Message } from "conference/components/meeting-page/room/types";
import { FormattedMessage } from "react-intl";

const Messages = () => {
    const theme = useTheme();
    const { messages } = useChatMessages();
    const roomSettings = useRoomSettings();
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollbarRef = useRef<any>(null);
    const [isScrolled, setScrolled] = useAsyncReference(false);
    const [lastSeenMsgCount, setLastSeenMsgCount] = useState(messages.length);
    const lastSeenMessage = useRef<Message>(null);

    const updateLastSeenMessage = () => {
        setLastSeenMsgCount(messages.length);
        if (messages.length) lastSeenMessage.current = messages[messages.length - 1];
    };

    useEffect(() => {
        updateLastSeenMessage();
    }, [isScrolled.current]);

    useEffect(() => {
        if (!isScrolled.current) updateLastSeenMessage();
    }, [messages.length]);

    useResizeObserver(containerRef, () => {
        if (!isScrolled.current) scrollbarRef.current.scrollToBottom();
    });

    useEffect(() => {
        roomSettings.lastSeenMessageId = messages[messages.length - 1]?.messageId ?? null;
    }, [messages.length]);

    const unseenMsgCount = messages.length - lastSeenMsgCount;

    const groupedMessages = createMessageGroups(messages);

    return (
        <React.Fragment>
            {Boolean(unseenMsgCount) && (
                <Box
                    sx={(theme) => ({
                        position: "absolute",
                        top: 0,
                        left: 5,
                        backgroundColor: theme.palette.info.main,
                        padding: "3px 6px",
                        width: "calc(100% - 10px)",
                        zIndex: 3,
                        color: theme.palette.info.contrastText,
                        fontWeight: "bold",
                        borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
                        transition: "top 0.3s",
                        cursor: "pointer",
                        transitionDelay: "0.1s",
                        opacity: 0.8,
                        backdropFilter: "blur(4px)",
                    })}
                    onClick={() => scrollbarRef.current.scrollToBottom()}
                >
                    <FormattedMessage
                        id="meetingPage.room.sidebar.chat.newMessagesBelow"
                        defaultMessage="{cnt, number} new {cnt, plural, one {message} other {messages}} since {since}"
                        values={{
                            cnt: unseenMsgCount,
                            since: lastSeenMessage.current?.date.format("HH:mm"),
                        }}
                    />
                </Box>
            )}
            <Scrollbar
                ref={scrollbarRef}
                style={{
                    width: "100%",
                    height: "100%",
                }}
                trackYProps={{
                    style: {
                        right: 3,
                        bottom: 2,
                        top: 2,
                        borderRadius: 3,
                        width: "4px !important",
                    },
                }}
                thumbYProps={{
                    style: {
                        cursor: "pointer",
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.text.primary, 0.3),
                    },
                }}
                onScrollStop={() => {
                    const sb = scrollbarRef.current;
                    setScrolled(sb.scrollHeight - sb.clientHeight - sb.scrollTop > 1);
                }}
            >
                <Box ref={containerRef}>
                    {groupedMessages.map((msgs) => (
                        <MessageGroup key={msgs[0].messageId} messages={msgs} />
                    ))}
                </Box>
            </Scrollbar>
        </React.Fragment>
    );
};

export default Messages;
